import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Languages, Zap, Facebook, Instagram } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/context/LanguageContext';
import { useSettings } from '@/context/SettingsContext';
import { cn } from '@/lib/utils';

export function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t, toggleLang, lang } = useLanguage();
  const { settings } = useSettings();
  const location = useLocation();

  const links = [
    { to: '/', label: t('nav.home') },
    { to: '/events', label: t('nav.events') },
    { to: '/studio', label: t('nav.studio') },
    { to: '/inquiry', label: t('nav.inquiry') },
    // Admin link removed for stealth access - accessible only via direct URL
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-strong">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="relative w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center border border-primary/20 group-hover:border-primary/40" style={{ transition: 'border-color 0.3s ease' }}>
              <Zap className="w-5 h-5 text-primary" />
              <div className="absolute inset-0 rounded-lg bg-primary/5 group-hover:bg-primary/10" style={{ transition: 'background 0.3s ease' }} />
            </div>
            <span className="text-lg font-semibold tracking-wider text-title" style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.15em', textTransform: 'uppercase' }}>
              The Ace
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {links.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={cn(
                  'px-4 py-2 rounded-md text-sm font-medium',
                  isActive(link.to)
                    ? 'text-primary bg-primary/10'
                    : 'text-body hover:text-title hover:bg-secondary'
                )}
                style={{ transition: 'color 0.2s ease, background 0.2s ease' }}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <a
              href={`https://wa.me/972${settings.contact_phone?.replace(/^0/, '').replace(/[-\s]/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-body hover:text-primary transition-colors duration-200"
              aria-label="WhatsApp"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
            </a>
            <a
              href="https://facebook.com/theace.studio"
              target="_blank"
              rel="noopener noreferrer"
              className="text-body hover:text-primary transition-colors duration-200"
              aria-label="Facebook"
            >
              <Facebook className="w-5 h-5" />
            </a>
            <a
              href="https://instagram.com/theace.studio"
              target="_blank"
              rel="noopener noreferrer"
              className="text-body hover:text-primary transition-colors duration-200"
              aria-label="Instagram"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleLang}
              className="text-body hover:text-primary hover:bg-primary/10"
              style={{ transition: 'color 0.2s ease, background 0.2s ease' }}
            >
              <Languages className="w-5 h-5" />
            </Button>
            <span className="hidden sm:block text-xs text-body">
              {lang === 'en' ? 'EN' : 'עב'}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden text-body hover:text-title"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass-strong border-t border-border overflow-hidden"
          >
            <div className="px-6 py-4 space-y-1">
              {links.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  className={cn(
                    'block px-4 py-3 rounded-md text-sm font-medium',
                    isActive(link.to)
                      ? 'text-primary bg-primary/10'
                      : 'text-body hover:text-title hover:bg-secondary'
                  )}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
